import React, { useEffect, useState } from "react";
import { KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { pillarApi, pillarErr, pillarDate } from "@/lib/pillarApi";
import { HowToFix } from "@/components/nds/HowToFix";
import { inputCls, labelCls, GoldButton, GhostButton, Pill, ErrorLine } from "@/components/nds/windows/resident/ui";

export function LifePassPin({ residentId }) {
  const [s, setS] = useState(null);
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => pillarApi.get(`/residents/${residentId}/lifepass-pin`).then(setS).catch(() => setS(null));
  useEffect(() => { load(); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const f = (e) => { if (e.detail?.id === residentId) load(); }; window.addEventListener("accounts:login-pin-changed", f); return () => window.removeEventListener("accounts:login-pin-changed", f); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true); setError(null);
    try {
      setS(await pillarApi.post(`/residents/${residentId}/lifepass-pin`, { pin, phone }));
      setPin(""); setPhone(""); toast.success("PACT Life Pass PIN saved — tell the resident their PIN in person");
    } catch (e) { setError(pillarErr(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this resident's PACT Life Pass PIN? They won't be able to sign in with it until you set a new one.")) return;
    try { setS(await pillarApi.del(`/residents/${residentId}/lifepass-pin`)); toast.success("PIN removed"); } catch (e) { setError(pillarErr(e)); }
  };
  if (!s) return null;
  return (
    <div data-testid="lifepass-pin" className="p-3 border border-white/10 rounded-sm space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <KeyRound className="h-3.5 w-3.5 text-nex-gold" />
        <span className={labelCls + " !mb-0"}>PACT Life Pass PIN sign-in</span>
        <Pill tone={s.has_pin ? "green" : "dim"} data-testid="lifepass-pin-status">{s.has_pin ? "PIN set" : "no PIN"}</Pill>
        {s.reset_requested_at && <Pill tone="yellow" data-testid="lifepass-pin-reset-requested">forgot PIN · asked {pillarDate(s.reset_requested_at)}</Pill>}
        {s.locked_until && <Pill tone="red" data-testid="lifepass-pin-locked">locked until {new Date(s.locked_until).toLocaleTimeString()}</Pill>}
        {!s.has_pin && <HowToFix code="no_pin" testid="fix-no-pin" />}
        {s.has_pin && <GhostButton data-testid="lifepass-pin-remove" className="ml-auto" onClick={remove}><Trash2 className="h-3 w-3" /> Remove PIN</GhostButton>}
      </div>
      <p data-testid="lifepass-pin-phone" className="text-xs text-nex-muted">
        Signs in with phone {s.phone_last4 ? `ending ${s.phone_last4}` : "— none on file yet"}{s.pin_set_at ? ` · PIN set ${pillarDate(s.pin_set_at)} by ${s.pin_set_by?.name || "—"}` : ""}
      </p>
      {s.has_pin && s.can_view_pin && (
        <p data-testid="lifepass-pin-current" className="text-xs text-nex-muted">
          Current PIN: {s.pin
            ? <span data-testid="lifepass-pin-value" className="font-mono text-sm text-nex-goldbright tracking-[0.3em]">{s.pin}</span>
            : <span data-testid="lifepass-pin-unknown">set before PINs could be shown — reset it to see it here</span>}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <input data-testid="lifepass-pin-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls + " !w-auto flex-1 min-w-[160px]"}
          placeholder={s.has_phone ? "New mobile number (optional)" : "Mobile number"} inputMode="tel" />
        <input data-testid="lifepass-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className={inputCls + " !w-32 font-mono tracking-[0.3em]"} placeholder="6 digits" inputMode="numeric" type="password" autoComplete="new-password" />
        <GoldButton data-testid="lifepass-pin-save" disabled={busy || pin.length !== 6} onClick={save}>{s.has_pin ? "Reset PIN" : "Set PIN"}</GoldButton>
      </div>
      <ErrorLine testId="lifepass-pin-error">{error}</ErrorLine>
      <p className="text-[11px] text-nex-muted">5 wrong PINs lock sign-in for 15 minutes. Setting a new PIN signs the app out on other phones.</p>
    </div>
  );
}
